"use client";

import { useQuery } from "@tanstack/react-query";
import { Suspense, useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import { useSearchParams } from "next/navigation";

import FormLabel from "@/components/form/form-label";
import MultiSelect from "@/components/form/multi-select";
import Pagination from "@/components/form/pagination";
import RCRange from "@/components/form/rc-slider-range";
import { RowsDropdown } from "@/components/form/rows";
import FetchError from "@/components/ui/fetch-error";
import Loading from "@/components/ui/loading";

import PublicationsTable from "./publication-table";

import api from "@/lib/api";

type FormValues = {
  keyword?: string;
  year?: number[];
  studies?: number[];
  page_size?: number;
  page?: number;
};

type PublicationsHomeProps = {
  studies: { value: number; label: string }[];
  year: number[];
};

type PublicationsDataProps = {
  count: number;
  next: string;
  previous: string;
  results: {
    id: number;
    title: string;
    source: string;
    studies: string[];
    year: number;
  }[];
};

export async function getPublicationsData(
  keyword: string | undefined,
  yearStart: number,
  yearEnd: number,
  study: number[] | undefined,
  page: number,
  page_size: number,
) {
  const queryParams = new URLSearchParams({
    page: page?.toString() || "1",
    page_size: page_size?.toString() || "10",
    keyword: keyword || "",
  });

  queryParams.append("year", `${yearStart},${yearEnd}`);
  queryParams.append("site_name", "hcap");

  if (study?.length) queryParams.append("studies", study.join(","));

  const response = await api.get<PublicationsDataProps>(`/pub/data/?${queryParams.toString()}`);
  return response;
}

export default function PublicationHome() {
  const [currentPage, setCurrentPage] = useState(1);
  const [tableData, setTableData] = useState<PublicationsDataProps>();
  const [loading, setLoading] = useState(false);

  const [yearSelection, setYearSelection] = useState<number[]>();

  const searchParams = useSearchParams();
  const initialKeyword = searchParams.get("keyword") || "";

  const params = new URLSearchParams({ site_name: "hcap" });

  const { handleSubmit, control, reset, watch, setValue } = useForm<FormValues>({
    defaultValues: {
      keyword: initialKeyword,
      year: [],
      studies: [],
      page_size: 10,
      page: 1,
    },
  });

  const keywordValue = watch("keyword");
  const studyValues = watch("studies");
  const page_size = watch("page_size");

  // Fetch Home Data
  const { data, error, isPending } = useQuery({
    queryKey: ["publications-hcap-home"],
    queryFn: () => api.get<PublicationsHomeProps>(`/pub/home/?${params.toString()}`),
  });

  // When data is available, update the form fields (non-blocking)
  useEffect(() => {
    if (data?.year) {
      setYearSelection(data.year);
    }
  }, [data, setValue]);

  const DEBOUNCE_DELAY = 300; // milliseconds

  useEffect(() => {
    const handler = setTimeout(() => {
      const fetchData = async () => {
        if (yearSelection && page_size) {
          const response = await getPublicationsData(
            keywordValue,
            yearSelection[0],
            yearSelection[1],
            studyValues,
            currentPage,
            page_size,
          );
          setTableData(response);
        }
      };
      fetchData();
    }, DEBOUNCE_DELAY);

    return () => {
      clearTimeout(handler);
    };
  }, [yearSelection, currentPage, page_size]);

  // Handle Form Submission
  const onSubmit: SubmitHandler<FormValues> = async (formData) => {
    const fullFormData = {
      ...formData,
      years: yearSelection,
    };
    try {
      if (fullFormData.years) {
        setLoading(true);
        setTableData(undefined);
        const response = await getPublicationsData(
          fullFormData.keyword || "",
          fullFormData.years?.[0],
          fullFormData.years?.[1],
          fullFormData.studies,
          fullFormData.page || 1,
          fullFormData.page_size || 10,
        );
        setTableData(response);
      } else {
        console.error("Both years are required.");
      }
    } catch (error) {
      console.error("Error fetching questionnaire data:", error);
    }
    setLoading(false);
  };

  // Reset Form
  const resetForm = () => {
    if (data) {
      reset({
        studies: [],
        keyword: "",
        page_size: 10,
        page: 1,
      });
      setCurrentPage(1);
      setYearSelection(data.year);
      setTableData(undefined);
      onSubmit({
        year: yearSelection,
        page: 1,
        page_size: 10,
      });
    }
  };

  if (isPending) return <Loading />;
  if (error) return <FetchError message={error.message} />;
  if (data?.year?.length !== 2) return <Loading />;

  return (
    <>
      <form className="row g-3 mb-4" onSubmit={handleSubmit(onSubmit)}>
        <div className="col-md-4 col-sm-6 mt-2">
          <FormLabel label="Keyword" />
          <input type="text" className="form-control" placeholder="Keyword" {...control.register("keyword")} />
        </div>

        <div className="col-md-4 col-sm-6 mt-2">
          <MultiSelect
            options={data?.studies || []}
            isPending={isPending}
            control={control}
            name="studies"
            label="Study"
          />
        </div>

        <div className="col-md-4 col-sm-6 mt-2">
          <RCRange
            label={"Year"}
            min={data.year[0]}
            max={data.year[1]}
            value={Array.isArray(yearSelection) ? yearSelection : []}
            onChange={(value) => setYearSelection(Array.isArray(value) ? value : [value])}
          />
        </div>

        <div className="col d-flex justify-content-end align-items-end">
          <RowsDropdown name="page_size" control={control} onSubmit={handleSubmit(onSubmit)} />
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={resetForm}
            style={{ marginRight: "1rem" }}
          >
            RESET
          </button>
          <button type="submit" className="btn g2-btn-blue">
            SEARCH
          </button>
        </div>
      </form>

      {loading && tableData == undefined ? (
        <Loading />
      ) : (
        tableData && (
          <Suspense fallback={<Loading />}>
            <PublicationsTable data={tableData} count={tableData.count} />
            {tableData.count > 0 && (
              <Pagination
                totalCount={tableData.count}
                page_size={page_size || 10}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                previous={tableData.previous}
                next={tableData.next}
              />
            )}
          </Suspense>
        )
      )}
    </>
  );
}
